import { OnboardingModalUI } from 'features';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { useFetchCountries, useFetchUser, useUpdateUser, usePostCompanyInfo } from 'hooks';
import { ImageUpload, Loader } from 'components';
import { howOptions } from 'features/registration/onboarding/utils';

/**
 * Interface for update user payload
 */
export interface UpdateUserPayload {
  username?: string;
  email?: string;
  phone_number?: string;
  first_name?: string;
  last_name?: string;
  country_code?: string;
  date_of_birth?: string;
  display_photo?: string;
  address?: string;
  referral?: string;
  role?: string;
  country?: string;
}

/**
 * Interface for company info payload
 */
export interface CompanyInfoPayload {
  name: string;
  industry: string;
  hiring_frequency: string;
  no_of_employees: string;
  company_logo?: string;
}

const OnboardingModal = ({ show }: { show: boolean }) => {
  // States
  const [stage, setStage] = useState(1);
  const [image, setImage] = useState({ show: false, type: 'company_logo' });
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [isSubmittingPersonal, setIsSubmittingPersonal] = useState(false);
  const [isSubmittingBusiness, setIsSubmittingBusiness] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Hooks
  const user = useAppSelector((state) => state.user);
  const {
    completedProfile,
    firstName,
    lastName,
    role,
    phone,
    referral,
    country,
    companyLogo: existingCompanyLogo,
    avatar: existingAvatar,
  } = user;
  const dispatch = useAppDispatch();
  const { getUser } = useFetchUser();
  const { fetchCountries, countries, loading: loadingCountries } = useFetchCountries();
  const { mutate: updateUser, isPending: isUpdatingUser } = useUpdateUser(dispatch, getUser, setImage, user);
  const { mutate: createCompany, isPending: isCreatingCompany } = usePostCompanyInfo(dispatch, getUser, user, setImage);

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    setStage(searchParams.get('incomplete') === 'business' ? 2 : 1);
  }, [searchParams]);

  // Update loading states based on mutation status
  useEffect(() => {
    setIsSubmittingPersonal(isUpdatingUser);
  }, [isUpdatingUser]);

  useEffect(() => {
    setIsSubmittingBusiness(isCreatingCompany);
  }, [isCreatingCompany]);

  const handleUpdateUser = (data: UpdateUserPayload) => {
    setIsSubmittingPersonal(true);
    updateUser(data, {
      onSuccess: () => {
        setIsSubmittingPersonal(false);
      },
      onError: () => {
        setIsSubmittingPersonal(false);
      }
    });
  };

  const handleCreateCompany = (data: CompanyInfoPayload) => {
    setIsSubmittingBusiness(true);
    const payload = imageBase64 ? { ...data, company_logo: imageBase64 } : data;
    createCompany(payload, {
      onSuccess: () => {
        setIsSubmittingBusiness(false);
        // Clear the search params to close the modal
        setSearchParams({});
      },
      onError: () => {
        setIsSubmittingBusiness(false);
      }
    });
  };

  const handleImageUpload = (data: string, rawImage?: File) => {
    // just save in state, don't make a request
    setCompanyLogo(rawImage ? URL.createObjectURL(rawImage) : null);
    setImageBase64(data);
    setImage({ show: false, type: '' });
  };

  const showLoader = loadingCountries;

  return (
    <>
      <Loader loading={showLoader} />
      <ImageUpload
        show={image.show}
        type={image.type}
        close={() => setImage({ show: false, type: '' })}
        submit={handleImageUpload}
      />
      <OnboardingModalUI
        show={show}
        submitPersonal={handleUpdateUser}
        submitBusiness={handleCreateCompany}
        stage={stage}
        setStage={setStage}
        countryOptions={countries}
        handleImageUpload={() => setImage({ show: true, type: 'company_logo' })}
        image={companyLogo}
        formImage={imageBase64}
        existingLogoURL={existingCompanyLogo}
        existingProfilePictureURL={existingAvatar}
        isSubmittingPersonal={isSubmittingPersonal}
        isSubmittingBusiness={isSubmittingBusiness}
        personalData={
          completedProfile
            ? {
              firstName,
              lastName,
              role,
              number: {
                code: phone.code,
                value: `${phone.code}${phone.number}`,
                number: phone.number,
              },
              how: howOptions.find((item) => item.value === referral) ?? {
                label: 'Others',
                value: 'others',
              },
              other: howOptions.find((item) => item.value === referral)
                ? undefined
                : referral,
              country: { label: country, value: country },
            }
            : undefined
        }
      />
    </>
  );
};

export { OnboardingModal };